import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import * as React from "react";
import {useState} from "react";

import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import type {ImageInput, ImageSource} from "@khan/guide-types";

import {ImageAttachModalWrapper} from "../image-attach/image-attach-modal-wrapper.tsx";
import {InputModeMenu} from "../image-attach/input-mode-menu.tsx";
import {useUserMayInputImages} from "../image-attach/use-user-may-input-images.ts";

import paperclip from "@phosphor-icons/core/regular/paperclip.svg";

interface LauncherProps {
    onAttachImages: (images: ImageInput[]) => void;
}

const AttachButton = ({
    disabled,
    openModal,
}: {
    disabled?: boolean;
    openModal?: () => void;
}) => (
    <IconButton
        disabled={disabled}
        icon={paperclip}
        aria-label={t`Attach image`}
        size="small"
        kind="tertiary"
        actionType="neutral"
        onClick={openModal}
        testId="image-attach-button"
    />
);

/**
 * A button that launches the image input modal.
 */
export function ImageInputModalLauncher({onAttachImages}: LauncherProps) {
    const userMayInputImages = useUserMayInputImages();
    const imageInputMenuFlag = useFeatureIsOn("image-input-menu");
    const [imageSource, setImageSource] = useState<ImageSource | null>(null);

    if (!userMayInputImages) {
        return null;
    }

    // New experience, currently behind a flag.
    if (imageInputMenuFlag) {
        if (!imageSource) {
            return (
                <Tooltip content={t`Attach image`}>
                    <InputModeMenu onOpenModal={setImageSource} />
                </Tooltip>
            );
        }

        return (
            <ModalLauncher
                modal={({closeModal}) => (
                    <ImageAttachModalWrapper
                        onCloseModal={() => {
                            setImageSource(null);
                            closeModal();
                        }}
                        onAttachImages={onAttachImages}
                        imageSource={imageSource}
                    />
                )}
                backdropDismissEnabled={true}
                opened={true}
                onClose={() => setImageSource(null)}
            />
        );
    }

    // Old experience, shown without flag.
    return (
        <ModalLauncher
            modal={({closeModal}) => (
                <ImageAttachModalWrapper
                    onCloseModal={closeModal}
                    onAttachImages={onAttachImages}
                />
            )}
            backdropDismissEnabled={true}
        >
            {({openModal}) => (
                <Tooltip content={t`Attach image`}>
                    <AttachButton openModal={openModal} />
                </Tooltip>
            )}
        </ModalLauncher>
    );
}
